/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Attributes from 'model/stats/Attributes';
import AttributeValues from 'model/stats/AttributeValues';

/**
 * Build the saving-throw proficiency strings (e.g. ["STR +5", "CON +4"]) from a class's
 * `prof_saving_throws` field, which Open5e delivers comma-SPACE delimited ("Strength, Constitution").
 * Each entry is trimmed before it is looked up on the attribute values — without trimming, the
 * second (space-prefixed) attribute name misses and its modifier comes out NaN.
 */
export const computeSavingThrows = (
  profSavingThrows: string,
  attributeValues: AttributeValues,
  proficiencyBonus: number,
): string[] => {
  const profs: string[] = [];
  for (const raw of (profSavingThrows || '').split(',')) {
    const name = raw.trim();
    if (!name) {
      continue;
    }
    const shortName = name.substring(0, 3).toUpperCase();
    const attr = name.toLowerCase() as keyof AttributeValues;
    const saveLevel = Attributes.getBonus(attributeValues[attr]) + proficiencyBonus;
    profs.push(`${shortName} +${saveLevel}`);
  }
  return profs;
};

/**
 * Compute a character's hit points, hit-dice string, and (average) hit-die type.
 * When a second class has a DIFFERENT hit die, each class's dice are summed separately (5e
 * multiclass rule) and the dice string is split (e.g. "5d10 + 5d6"); otherwise the combined
 * single-die form is used ("10d10").
 */
export const computeHitDice = (params: {
  primaryHitDie: number;
  primaryLevel: number;
  hitDiceBonus: number;
  secondaryHitDie?: number | null;
  secondaryLevel?: number;
}): { hitPoints: number; hitDice: string; hitDiceType: number } => {
  const { primaryHitDie, primaryLevel, hitDiceBonus } = params;
  const secondaryHitDie = params.secondaryHitDie ?? null;
  // A second class only contributes levels when it actually exists.
  const secondaryLevel = secondaryHitDie != null ? (params.secondaryLevel ?? 0) : 0;
  const combinedLevel = primaryLevel + secondaryLevel;
  const hitDiceBonusStr = (hitDiceBonus === 0) ? '' : ((hitDiceBonus > 0 ? ' +' : ' ') + hitDiceBonus);

  let hitDiceType = primaryHitDie;
  // First level (of the primary class) always gets the full hit-die value.
  let hitPoints = primaryHitDie + hitDiceBonus + (combinedLevel - 1) * (primaryHitDie / 2 + 1);
  let hitDice = `${combinedLevel}d${primaryHitDie}${hitDiceBonusStr}`;

  if (secondaryHitDie != null && primaryHitDie !== secondaryHitDie) {
    hitPoints = primaryHitDie + hitDiceBonus
      + (primaryLevel - 1) * (primaryHitDie / 2 + 1)
      + secondaryLevel * (secondaryHitDie / 2 + 1);
    hitDice = `${primaryLevel}d${primaryHitDie} + ${secondaryLevel}d${secondaryHitDie}${hitDiceBonusStr}`;
    hitDiceType = (primaryHitDie + secondaryHitDie) / 2;
  }

  return { hitPoints, hitDice, hitDiceType };
};
